/**
 * Player feedback: what a phone may send, and the GitHub issue that comes out
 * of it. Pure on purpose — the server owns the token and the network call, but
 * the shape of a submission and the wording of the issue are rules, so they
 * live here and are asserted at tier 0.
 *
 * Everything a player typed is treated as hostile text. It is clamped, control
 * characters are dropped, and the body carries it inside a fence long enough
 * that nothing inside can escape it — which is also what keeps an `@name` in a
 * report from paging a stranger when the issue is filed.
 */

/** What the form offers. `bug` is "this is broken", `feature` is "add this". */
export const FEEDBACK_KINDS = ['bug', 'feature'] as const;

export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

export function isFeedbackKind(value: unknown): value is FeedbackKind {
  return typeof value === 'string' && (FEEDBACK_KINDS as readonly string[]).includes(value);
}

/** Long enough for a real bug report, short enough that nobody pastes a novel. */
export const MAX_FEEDBACK_MESSAGE = 2_000;
/** A player writes far less than this; a spammer is cut off here. */
export const MIN_FEEDBACK_MESSAGE = 4;
/** "find me on discord as …" — an optional way back to the reporter. */
export const MAX_FEEDBACK_CONTACT = 120;
/** Each context field is a machine-written breadcrumb, never prose. */
const MAX_CONTEXT_FIELD = 200;

/**
 * Where the reporter was standing. The client fills what it knows and omits
 * the rest; every field is a breadcrumb for reproducing, never identity.
 */
export interface FeedbackContext {
  /** The route they were on: `/play`, `/profile`. Query strings are dropped. */
  path?: string;
  /** `buildLabel()` from the web app — which build the bug was seen on. */
  build?: string;
  /** The venue and table they were at, when they were at one. */
  casinoCode?: string;
  tableCode?: string;
  /** The game on the felt: `blackjack`, `craps`, … */
  gameType?: string;
  /** `390×844` — layout bugs are mostly screen-size bugs. */
  viewport?: string;
}

/** A submission that has already been through `readFeedback`. */
export interface FeedbackSubmission {
  kind: FeedbackKind;
  message: string;
  /** How to reach them back, or null when they would rather not say. */
  contact: string | null;
  context: FeedbackContext;
}

/** Printable characters only: newlines survive, terminal escapes do not. */
const clean = (raw: unknown, limit: number): string =>
  typeof raw === 'string'
    ? raw
        .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '')
        .replace(/\r\n?/g, '\n')
        .trim()
        .slice(0, limit)
    : '';

/** One line, for a title or a table cell. */
const oneLine = (raw: unknown, limit: number): string =>
  clean(raw, limit).replace(/\s+/g, ' ').trim();

const readContext = (raw: unknown): FeedbackContext => {
  const source = (raw ?? {}) as Record<string, unknown>;
  const context: FeedbackContext = {};
  for (const key of ['path', 'build', 'casinoCode', 'tableCode', 'gameType', 'viewport'] as const) {
    const value = oneLine(source[key], MAX_CONTEXT_FIELD);
    if (value) context[key] = value;
  }
  return context;
};

/**
 * A submission off the wire, read leniently: unknown fields are ignored, long
 * ones clamped, and a message that is missing or too short is refused outright
 * — an empty report is worse than no report, because someone has to triage it.
 */
export function readFeedback(raw: unknown): FeedbackSubmission | null {
  const source = (raw ?? {}) as Record<string, unknown>;
  if (!isFeedbackKind(source.kind)) return null;
  const message = clean(source.message, MAX_FEEDBACK_MESSAGE);
  if (message.length < MIN_FEEDBACK_MESSAGE) return null;
  const contact = oneLine(source.contact, MAX_FEEDBACK_CONTACT);
  return {
    kind: source.kind,
    message,
    contact: contact || null,
    context: readContext(source.context),
  };
}

/**
 * A code fence that the given text cannot break out of: one backtick longer
 * than the longest run inside it, and never shorter than three.
 */
export function fenceFor(text: string): string {
  const longest = Math.max(0, ...[...text.matchAll(/`+/g)].map((m) => m[0].length));
  return '`'.repeat(Math.max(3, longest + 1));
}

/** The issue title: the kind, then the reporter's opening line, clipped. */
export function feedbackTitle(sub: FeedbackSubmission): string {
  const prefix = sub.kind === 'bug' ? 'Bug' : 'Feature';
  const first = oneLine(sub.message.split('\n')[0] ?? '', 200);
  const clipped = first.length > 72 ? `${first.slice(0, 71).trimEnd()}…` : first;
  return `${prefix}: ${clipped}`;
}

/**
 * The labels the issue is filed under. `feedback` marks where it came from;
 * the second is GitHub's own default for the kind, so the repo's existing
 * triage views pick it up without new configuration.
 */
export function feedbackLabels(sub: FeedbackSubmission): string[] {
  return ['feedback', sub.kind === 'bug' ? 'bug' : 'enhancement'];
}

/** What the server knows and the phone does not, stamped onto the issue. */
export interface FeedbackStamp {
  /** ISO timestamp the server received it. */
  at: string;
  /** The account name, when one was signed in. Guests report anonymously. */
  reporter?: string | null;
  /** The feedback row's id, so a filed issue can be traced back to the DB. */
  ref?: string;
}

const CONTEXT_LABELS: [keyof FeedbackContext, string][] = [
  ['path', 'Page'],
  ['build', 'Build'],
  ['gameType', 'Game'],
  ['casinoCode', 'Casino'],
  ['tableCode', 'Table'],
  ['viewport', 'Screen'],
];

/** Pipes would split a markdown table cell; backticks would break its code span. */
const cell = (value: string): string => value.replace(/\|/g, '\\|').replace(/`/g, "'");

/**
 * The whole issue body. The reporter's words come first and verbatim, inside
 * the fence; everything after it is machine-written context a triager reads
 * second.
 */
export function feedbackBody(sub: FeedbackSubmission, stamp: FeedbackStamp): string {
  const fence = fenceFor(sub.message);
  const rows: [string, string][] = [];
  for (const [key, label] of CONTEXT_LABELS) {
    const value = sub.context[key];
    if (value) rows.push([label, value]);
  }
  rows.push(['Reported', stamp.at]);
  rows.push(['Reporter', stamp.reporter ? stamp.reporter : 'Guest (not signed in)']);
  if (stamp.ref) rows.push(['Ref', stamp.ref]);
  if (sub.contact) rows.push(['Contact', sub.contact]);

  const table = ['| | |', '| --- | --- |', ...rows.map(([k, v]) => `| ${k} | \`${cell(v)}\` |`)];

  return [
    sub.kind === 'bug'
      ? 'A player reported this from the in-app feedback form.'
      : 'A player asked for this from the in-app feedback form.',
    '',
    `${fence}`,
    sub.message,
    `${fence}`,
    '',
    ...table,
    '',
    '<sub>Filed automatically by TV Casino. The quoted text is the player’s own.</sub>',
  ].join('\n');
}

/** Title, body and labels together — what the GitHub issue call needs. */
export interface FeedbackIssue {
  title: string;
  body: string;
  labels: string[];
}

export function feedbackIssue(sub: FeedbackSubmission, stamp: FeedbackStamp): FeedbackIssue {
  return {
    title: feedbackTitle(sub),
    body: feedbackBody(sub, stamp),
    labels: feedbackLabels(sub),
  };
}
